import type { Table } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';

import { featureFlags } from '@/configs/featureFlags';
import { Translation } from '@/hoslog-intl';
import { ChevronLeft } from '@/icons';
import { cn } from '@/lib/utils';
import { Button } from 'ui/button';
import Select from './TableSelect';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100].map((pageSize) => ({
  label: String(pageSize),
  value: String(pageSize),
}));

type TableMetaWithPagination = {
  totalPage?: number;
  pageSize?: number;
};

interface DataTablePaginationProps<TData> extends React.ComponentProps<'div'> {
  table: Table<TData> & {
    options: {
      meta?: TableMetaWithPagination;
    };
  };
  pageSizeOptions?: number[];
}

export function DataTablePagination<TData>({ table, className, ...props }: DataTablePaginationProps<TData>) {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.options.meta?.pageSize ?? table.getState().pagination.pageSize;
  const totalRows = table.options.meta?.totalPage || 0;
  let start = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  start = Math.min(start, totalRows);
  const end = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <div
      className={cn(
        'flex w-full flex-wrap items-center justify-end border-t border-t-surface-hover border-b-2 border-b-muted gap-4 overflow-auto px-4 py-3',
        className,
      )}
      {...props}
    >
      <div className="flex flex-wrap items-center justify-end gap-4">
        {featureFlags.itemsPerPage && (
          <div className="flex items-center whitespace-nowrap text-xs font-normal text-primary">
            <span>
              <Translation id="common.form.label.show" />
            </span>
            <Select
              options={PAGE_SIZE_OPTIONS}
              value={String(table.getState().pagination.pageSize)}
              onValueChange={(value) => {
                table.setPagination({ pageIndex: 0, pageSize: Number(value) });
              }}
              hideDefaultOption
              className="w-20 mr-2 py-0 h-8 cursor-pointer"
            />
            <span>
              <Translation id="common.form.label.entries" />
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 whitespace-nowrap">
          <Button
            aria-label="Go to previous page"
            variant="icon"
            size="icon"
            className="size-4"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </Button>
          <div className="text-xs font-medium ">{`${start}-${end}件を表示中 /${totalRows}件`}</div>
          <Button
            aria-label="Go to next page"
            variant="icon"
            size="icon"
            className="size-4"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
